import { useCallback, useEffect } from "react";
import { ReactComponent as CloseIcon } from "../../../Assets/ActionIcons/CloseIcon.svg";

export type ModalSize = "x-small" | "small" | "medium" | "large" | "full";

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCancel?: () => void;
  onOk?: () => void;
  title?: string | React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode | null;
  size?: ModalSize;
  hideCloseButton?: boolean;
  hideCancelButton?: boolean;
  closeOnOverlayClick?: boolean;
  closeOnEsc?: boolean;
  cancelText?: string;
  okText?: string;
  closable?: boolean;
  disableOkButton?: boolean;
  mask?: boolean;
  width?: string | number;
  zIndex?: number;
  topOffset?: string | number;
}

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  onCancel,
  onOk,
  title,
  children,
  footer,
  size,
  hideCloseButton = false,
  hideCancelButton = false,
  closeOnOverlayClick = true,
  closeOnEsc = true,
  cancelText = "Cancel",
  okText = "Ok",
  closable = true,
  disableOkButton = false,
  mask = true,
  width,
  zIndex = 50,
  topOffset
}) => {
  const handleClose = useCallback(() => {
    if (onCancel) onCancel();
    onClose();
  }, [onCancel, onClose]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === "Escape" && closeOnEsc) {
        handleClose();
      }
    },
    [handleClose, closeOnEsc]
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
    }
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  return (
    <div
      className={`fixed inset-0 flex ${
        topOffset ? "items-start" : "items-center"
      } justify-center ${mask ? "bg-black bg-opacity-50" : ""}`}
      style={{ zIndex }}
      onClick={closeOnOverlayClick ? handleClose : undefined}
    >
     <div
  className={`bg-white rounded-lg shadow-lg p-5 relative overflow-y-auto ${
    width
      ? width
      : size === "x-small"
        ? "w-[20rem]"
        : size === "small"
          ? "w-[30rem]"
          : size === "medium"
            ? "w-[40rem]"
            : size === "large"
              ? "w-[50rem]"
              : size === "full"
                ? "w-full"
                : ""
  }`}
  onClick={e => e.stopPropagation()}
  style={{
    ...(topOffset ? { marginTop: `${topOffset}` } : {}),
  }}
>

        {closable && !hideCloseButton && (
          <button
            className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
            onClick={handleClose}
          >
            <CloseIcon className="w-3 h-3" />
          </button>
        )}
        {title && typeof title !== "string" ? (
          <>{title}</>
        ) : (
          <h2 className="text-xl font-semibold mb-4">{title}</h2>
        )}
        <div className={footer !== null ? "mb-4" : ""}>{children}</div>
        {footer !== null ? (
          <div className={"mt-4 flex justify-end space-x-2"}>
            {footer ? (
              footer
            ) : (
              <>
                {!hideCancelButton && (
                  <button
                    className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
                    onClick={handleClose}
                  >
                    {cancelText}
                  </button>
                )}
                <button
                  disabled={disableOkButton}
                  className={`px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-500 ${
                    disableOkButton ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                  onClick={onOk}
                >
                  {okText}
                </button>
              </>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default Modal;
